import React, { useRef, useState } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { useApp } from "context";
import { PairValue } from "types";
import { Constants, Helpers } from "utils";

type Props = {
  sx?: any;
  id?: string;
  name: string;
  label?: string;
  options: PairValue[];
  placeholder?: string;
  onChange?: (name: string, value: any) => void;
};

export const MentionTextField = (props: Props) => {
  const elm = useRef<any>(null);
  const { t, theme } = useApp();
  const [value, setValue] = useState<string>("");
  const [options, setOptions] = useState<PairValue[]>([]);

  function handleChange() {
    props.onChange && props.onChange(props.name, value);
    setValue("");
    setOptions([]);
  }

  function handleInputChange(_: string, newValue: any) {
    setValue(newValue);
  }

  function handleSelectPatient(event: any, newValue: any) {
    const element = elm.current.querySelector("input");
    const selectionStart = element.selectionStart;
    const selectionEnd = element.selectionEnd;
    setOptions([]);
    setValue((prevValue: string) => {
      if (Helpers.hasValue(value)) {
        const prev = value.slice(0, selectionStart);
        const next = value.slice(selectionStart + 1);
        return prev + prevValue + next;
      } else return "";
    });
  }

  function renderInput(params: any) {
    return (
      <TextField
        ref={elm}
        sx={{
          width: "100%",
          backgroundColor: theme.palette.background.color,
          "& label": {
            color: theme.palette.font.color,
          },
          "& .MuiTextField-root": {
            backgroundColor: theme.palette.background.color,
            "&:hover": {
              backgroundColor: theme.palette.background.color,
            },
          },
          "& .MuiInputBase-root": {
            backgroundColor: theme.palette.background.color,
          },
          "& .MuiInputBase-input": {
            color: theme.palette.background.text,
            borderRadius: theme.border.radius / 4,
            backgroundColor: theme.palette.background.color,
            borderBottomWidth: 2,
            borderBottomStyle: "solid",
            borderBottomColor: theme.palette.background.color,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            "&:focus": {
              borderBottomColor: theme.color.primary.color,
            },
          },
          "& .MuiInputBase-inputMultiline": {
            color: theme.palette.font.color,
          },
          "& input": {
            color: theme.palette.font.color,
          },
          "& .MuiFilledInput-root": {
            padding: 0,
            "& .MuiFilledInput-input": {
              padding: theme.margin.md,
            },
          },
          "& .Mui-disabled": {
            color: theme.color.disabled.color,
            backgroundColor: theme.palette.background.color,
            WebkitTextFillColor: theme.color.disabled.color,
          },
          "& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator":
            {
              color: theme.palette.font.color,
            },
        }}
        {...params}
        hiddenLabel
        name={props.name}
        placeholder={props.placeholder}
        variant="filled"
        fullWidth
      />
    );
  }

  return (
    <Autocomplete
      id={props.id}
      sx={{
        width: "100%",
      }}
      options={options.map((item: PairValue) => item)}
      getOptionLabel={(option: PairValue | string) =>
        typeof option === "string" ? option : option?.value
      }
      renderInput={renderInput}
      inputValue={value}
      value={value}
      noOptionsText={t.message.placeholder.no_option}
      onInputChange={(event: any, newValue: any) => {
        handleInputChange(props.name, newValue);
        const caret = event.target.selectionStart;

        if (newValue && newValue[caret - 1] === "@") {
          const inputParts = newValue.split("@");
          const lastInputPart = inputParts[inputParts.length - 1];
          const indexOfLastInputPart = caret - 1 + lastInputPart.indexOf(" ");

          if (indexOfLastInputPart !== -1) {
            const filterValue = lastInputPart.slice(
              0,
              lastInputPart.indexOf(" ")
            );
            const filteredOptions = props.options.filter((option: PairValue) =>
              option.value.toLowerCase().includes(filterValue.toLowerCase())
            );
            setOptions(filteredOptions);
          } else {
            setOptions([]);
          }
        } else {
          setOptions([]);
        }
      }}
      freeSolo
      onChange={handleSelectPatient}
      onBlur={handleChange}
      onKeyDown={(event: any) =>
        event.which === Constants.KEYCODE.ENTER && handleChange()
      }
      filterOptions={(options: any) => options}
      selectOnFocus={false}
      handleHomeEndKeys
      renderOption={(props: any, option: any) => {
        const { inputValue = "" } = option;
        const index = option.value
          .toLowerCase()
          .indexOf(inputValue.toLowerCase());

        if (index !== -1) {
          return (
            <li {...props}>
              {option.value.slice(0, index)}
              <b>{option.value.slice(index, index + inputValue.length)}</b>
              {option.value.slice(index + inputValue.length)}
            </li>
          );
        } else {
          return <li {...props}>{option.value}</li>;
        }
      }}
    />
  );
};
